/**
 * useNotificationListener Hook for SCO Web App
 *
 * React hook for listening to real-time notifications via WebSocket
 *
 * Usage:
 *
 * import { useNotificationListener } from './utils/ws/useNotificationListener';
 *
 * function MyComponent() {
 *   const { notificationCount } = useNotificationListener({
 *     onNotification: (notification) => {
 *       console.log('New notification:', notification);
 *       // Update UI, show toast, etc.
 *     },
 *     onChatMessage: (notif) => {
 *       // Handle chat notification
 *     },
 *     onTaskStatusChange: (notif) => {
 *       // Handle task status change
 *     }
 *   });
 *
 *   return <div>Unread: {notificationCount}</div>;
 * }
 */

import { useEffect, useRef, useState } from 'react';
import { WsClient } from './wsClient';

let globalWsClient = null;
let notificationHandlers = new Set();

const isWsDebugEnabled = () => {
  try {
    return localStorage.getItem("SCO_WS_DEBUG") === "1";
  } catch (_) {
    return false;
  }
};

const debugLog = (...args) => {
  if (isWsDebugEnabled()) {
    console.log(...args);
  }
};

const debugWarn = (...args) => {
  if (isWsDebugEnabled()) {
    console.warn(...args);
  }
};

/**
 * Get or create the global WebSocket client
 */
export function getGlobalWsClient() {
  if (!globalWsClient) {
    debugLog('[SCO-NotifWS] Creating global WebSocket client...');
    globalWsClient = new WsClient({
      onNotification: ({ notification }) => {
        debugLog('[SCO-NotifWS] Notification received:', {
          notif_id: notification.notif_id,
          type: notification.type,
          user_id: notification.user_id,
          task_id: notification.task_id,
          description: notification.description?.substring(0, 50)
        });
        debugLog('[SCO-NotifWS] Broadcasting to', notificationHandlers.size, 'handlers');

        let handlerCount = 0;
        notificationHandlers.forEach(handler => {
          try {
            handlerCount++;
            debugLog(`[SCO-NotifWS] Calling handler #${handlerCount}`);
            handler(notification);
          } catch (e) {
            console.error(`[SCO-NotifWS] ❌ Handler #${handlerCount} error:`, e);
          }
        });
        debugLog('[SCO-NotifWS] Finished broadcasting');
      },
      onAuthAck: (msg) => {
        debugLog('[SCO-NotifWS] Authenticated user_id:', msg.user_id);
        debugLog('[SCO-NotifWS] Auto-subscribed to channel: user:' + msg.user_id);
      },
      onReady: () => {
        debugLog('[SCO-NotifWS] WebSocket ready');
      },
      onError: (e) => {
        console.error('[SCO-NotifWS] ❌ WebSocket ERROR:', e);
      },
      onClose: () => {
        debugWarn('[SCO-NotifWS] WebSocket closed, reconnecting');
      },
      logPrefix: '[SCO-NotifWS]',
    });

    // Auto-connect
    debugLog('[SCO-NotifWS] Starting WebSocket connection');
    globalWsClient.connect();
  }
  return globalWsClient;
}

/**
 * React hook for listening to real-time notifications
 *
 * @param {Object} options - Handler options
 * @param {Function} options.onNotification - Called for all notifications
 * @param {Function} options.onChatMessage - Called for "New Message" notifications
 * @param {Function} options.onTaskStatusChange - Called for status change notifications
 * @param {Function} options.onTaskAssigned - Called for "Task Assigned" notifications
 * @param {Function} options.onInvoiceSent - Called for "Invoice Sent" notifications
 * @param {boolean} options.autoConnect - Whether to auto-connect (default: true)
 * @returns {Object} - { notificationCount }
 */
export function useNotificationListener({
  onNotification,
  onChatMessage,
  onTaskStatusChange,
  onTaskAssigned,
  onInvoiceSent,
  autoConnect = true,
} = {}) {
  const [notificationCount, setNotificationCount] = useState(0);
  const handlersRef = useRef({
    onNotification,
    onChatMessage,
    onTaskStatusChange,
    onTaskAssigned,
    onInvoiceSent,
  });

  // Update refs when handlers change
  useEffect(() => {
    handlersRef.current = {
      onNotification,
      onChatMessage,
      onTaskStatusChange,
      onTaskAssigned,
      onInvoiceSent,
    };
  }, [onNotification, onChatMessage, onTaskStatusChange, onTaskAssigned, onInvoiceSent]);

  useEffect(() => {
    if (!autoConnect) return;

    // Ensure WebSocket is connected
    getGlobalWsClient();

    // Create our notification handler
    const handler = (notification) => {
      const handlers = handlersRef.current;

      debugLog('[SCO-useNotificationListener] Notification received:', {
        notif_id: notification.notif_id,
        type: notification.type,
        hasGenericHandler: !!handlers.onNotification
      });

      // Increment count
      setNotificationCount(prev => prev + 1);

      // Call generic handler
      if (handlers.onNotification) {
        debugLog('[SCO-useNotificationListener] Calling generic onNotification handler');
        handlers.onNotification(notification);
      }

      // Call type-specific handlers
      switch (notification.type) {
        case 'New Message':
          if (handlers.onChatMessage) {
            debugLog('[SCO-useNotificationListener] Calling onChatMessage handler');
            handlers.onChatMessage(notification);
          }
          break;

        case 'Updated Task Status':
        case 'updated task status':
          if (handlers.onTaskStatusChange) {
            debugLog('[SCO-useNotificationListener] Calling onTaskStatusChange handler');
            handlers.onTaskStatusChange(notification);
          }
          break;

        case 'Task Assigned':
          if (handlers.onTaskAssigned) {
            debugLog('[SCO-useNotificationListener] Calling onTaskAssigned handler');
            handlers.onTaskAssigned(notification);
          }
          break;

        case 'Invoice Sent':
          if (handlers.onInvoiceSent) {
            debugLog('[SCO-useNotificationListener] Calling onInvoiceSent handler');
            handlers.onInvoiceSent(notification);
          }
          break;

        default:
          debugLog('[SCO-useNotificationListener] Unknown type:', notification.type);
      }
    };

    // Register our handler
    notificationHandlers.add(handler);
    debugLog('[useNotificationListener] Registered. Total handlers:', notificationHandlers.size);

    // Cleanup
    return () => {
      notificationHandlers.delete(handler);
      debugLog('[useNotificationListener] Unregistered. Total handlers:', notificationHandlers.size);
    };
  }, [autoConnect]);

  return {
    notificationCount,
    resetCount: () => setNotificationCount(0),
  };
}

/**
 * Disconnect WebSocket (e.g., on logout)
 */
export function disconnectNotifications() {
  if (globalWsClient) {
    globalWsClient.close();
    globalWsClient = null;
    notificationHandlers.clear();
    debugLog('[NotificationListener] Disconnected');
  }
}

/**
 * Reconnect WebSocket (e.g., after login)
 */
export function reconnectNotifications() {
  if (globalWsClient) {
    globalWsClient.connect();
  } else {
    getGlobalWsClient();
  }
}

export default useNotificationListener;
